import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';

import { Button } from './shadcn/button';
import { Textarea } from './shadcn/textarea';
import {
  FEEDBACK_QUESTIONS,
  FeedbackQuestionIds,
  FeedbackResponseType,
} from '../../../../backend/src/feedback/feedback-questions';
import { useSubmitFeedback } from '@/api/mutations/useSubmitFeedback';
import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';
import { useFeedbackStore } from '@/new/stores/feedback-store';

const FREETEXT_LENGTH_LIMIT = 1500;

export function FeedbackPopup() {
  const { request, closeFeedback } = useFeedbackStore();
  const { submitFeedback } = useSubmitFeedback();

  const [response, setResponse] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const freeTextOverLimit = freeText.length > FREETEXT_LENGTH_LIMIT;

  const question = request ? FEEDBACK_QUESTIONS[request.questionId] : null;

  const handleClose = () => {
    closeFeedback();
    setResponse(null);
    setFreeText('');
    setSubmitted(false);
  };

  const handleSubmit = () => {
    if (!response || !request) return;
    submitFeedback.mutate(
      { questionId: request.questionId, response, freeText: freeText || undefined, metadata: request.metadata },
      {
        onSuccess: () => setSubmitted(true),
        onError: () => {
          toast.error('Failed to submit feedback. Please try again.');
        },
      },
    );
  };

  const isOpen = !!request && !!question;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed right-4 bottom-4 z-50 w-96 rounded-xl border border-gray-200 bg-white p-4.5 shadow-xl/6"
        >
          <div className="mb-3 flex items-center justify-between border-b border-dashed pb-4">
            <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <Icon className="lucide--message-circle text-teal-500" /> Feedback
            </h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <Icon className="lucide--x" />
            </button>
          </div>

          <div className="relative">
            <motion.div
              animate={{ opacity: submitted ? 0 : 1, filter: submitted ? 'blur(5px)' : 'blur(0px)' }}
              transition={{ duration: 0.2 }}
              aria-hidden={submitted}
              className="flex flex-col gap-4"
            >
              <p className="mt-1 text-gray-600">{question!.question}</p>

              <div className="mb-3">
                <ResponseInput question={question!} value={response} onChange={setResponse} />
              </div>

              {question!.allowsFreeText && (
                <Textarea
                  placeholder="Any additional comments? (optional)"
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  lengthLimit={FREETEXT_LENGTH_LIMIT}
                  className="max-h-72 min-h-20 text-[13px]!"
                />
              )}

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!response || freeTextOverLimit || submitFeedback.isLoading}
                >
                  Submit
                  <Icon className="lucide--chevron-right -ml-0.5" />
                </Button>
              </div>
            </motion.div>

            <AnimatePresence>
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, filter: 'blur(5px)' }}
                  animate={{ opacity: 1, filter: 'blur(0px)' }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-xs text-gray-500"
                >
                  <p>Thank you for leaving feedback.</p>

                  <p className="text-center text-balance">
                    Reach out to{' '}
                    <a href="mailto:support@wexler.ai" className="text-blue-500">
                      support@wexler.ai
                    </a>{' '}
                    if you have any more questions — we're happy to help.
                  </p>

                  <Button size={'sm'} className="mt-4" onClick={handleClose}>
                    Dismiss
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type QuestionDef = (typeof FEEDBACK_QUESTIONS)[FeedbackQuestionIds];

const ResponseInput = ({
  question,
  value,
  onChange,
}: {
  question: QuestionDef;
  value: string | null;
  onChange: (value: string) => void;
}) => {
  if (question.responseType === FeedbackResponseType.Thumbs) {
    return (
      <div className="mt-1 flex justify-center gap-2">
        <Button
          variant={value === 'thumbs-up' ? 'primary' : 'default'}
          size="icon"
          onClick={() => onChange('thumbs-up')}
          className={cn(
            'px-3.5 py-2 text-lg hover:border-teal-400 hover:bg-teal-50',
            value === 'thumbs-up' && 'border-teal-400 bg-teal-50 text-teal-600',
          )}
        >
          👍
        </Button>
        <Button
          variant={value === 'thumbs-down' ? 'destructive' : 'default'}
          size="icon"
          onClick={() => onChange('thumbs-down')}
          className={cn(
            'px-3.5 py-2 text-lg hover:border-red-300 hover:bg-red-50',
            value === 'thumbs-down' && 'border-red-300 bg-red-50 text-red-500',
          )}
        >
          👎
        </Button>
      </div>
    );
  }

  if (question.responseType === FeedbackResponseType.Rating) {
    return (
      <div className="relative flex flex-col">
        <div className="mx-3 mt-6 flex items-center justify-between rounded-full border bg-gray-50 p-0.25 px-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => onChange(String(n))}
              style={{ anchorName: `--rating-${n}` } as React.CSSProperties}
              className="group relative -my-3 flex flex-col items-center px-2 py-3"
            >
              <span
                className={cn(
                  'absolute -top-2.5 text-xs text-gray-400 duration-200 group-hover:text-gray-500',
                  value === String(n) && '-top-3.5 text-teal-500',
                )}
              >
                {n}
              </span>
              <div className={cn('m-1 size-1.25 rounded-full bg-gray-300 transition-colors group-hover:bg-gray-400')} />
            </button>
          ))}

          <AnimatePresence>
            {value && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute size-5.75 rounded-full border-5 border-white bg-teal-400 shadow-lg/8"
                style={
                  {
                    positionAnchor: `--rating-${value}`,
                    top: 'anchor(center)',
                    left: 'anchor(center)',
                    translate: '-50% -50%',
                    transitionProperty: 'top, left, overlay, display',
                    transitionDuration: '0.25s',
                    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                    transitionBehavior: 'allow-discrete',
                  } as React.CSSProperties
                }
              />
            )}
          </AnimatePresence>
        </div>

        <div className="mt-2.5 flex justify-between px-1">
          <span className="text-xs text-gray-400">{question.lowLabel}</span>
          <span className="text-xs text-gray-400">{question.highLabel}</span>
        </div>
      </div>
    );
  }

  if (question.responseType === FeedbackResponseType.Options) {
    return (
      <div className="flex flex-col flex-wrap gap-1.5">
        {question.options.map((option) => (
          <Button
            key={option}
            variant={value === option ? 'primary' : 'default'}
            size="sm"
            className={cn('rounded-lg py-1', value === option && 'hover:border-teal-400 hover:bg-teal-50')}
            onClick={() => onChange(option)}
          >
            {option}
          </Button>
        ))}
      </div>
    );
  }

  return null;
};
